import React from 'react';
import { Platform, StyleProp, StyleSheet, Text, ViewStyle } from 'react-native';
import { Link } from 'expo-router';
import Banner from '../ui/Banner';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import {
    type ProjectCreationEntitlementSnapshot,
    requiresSubscriptionForProjectCreation,
    projectCreationSubscriptionMessage,
    projectCreationTrialLabel,
} from '../../lib/projectCreationEntitlements';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import { getLegacyUpgradeAction, isLegacyAccessEnded } from '../../lib/legacyTransition';
import { msg } from '@lingui/core/macro';
import {
    type LocalizedMessage,
    useResolvedMessage,
} from '../../lib/i18n/messages';

interface ProjectCreationUpgradeBannerProps {
    visible?: boolean;
    dismissable?: boolean;
    onDismiss?: () => void;
    style?: StyleProp<ViewStyle>;
    snapshot?: ProjectCreationEntitlementSnapshot | null;
    message?: LocalizedMessage;
    testID?: string;
}

export default function ProjectCreationUpgradeBanner({
    visible = true,
    dismissable = true,
    onDismiss,
    style,
    snapshot,
    message = msg({ id: 'billing.projectLimit.prompt', message: 'Want to create another topic?' }),
    testID,
}: ProjectCreationUpgradeBannerProps) {
    const { fontsLoaded } = useFontLoading();
    const entitlementsQuery = useCurrentEntitlements({ enabled: false });
    const requiresSubscription = requiresSubscriptionForProjectCreation(snapshot);
    const unavailable = !snapshot || snapshot.derived?.entitlement_fallback === true;
    const resolvedMessage = useResolvedMessage(unavailable
        ? msg({ id: 'projectCreation.accessUnavailable', message: 'Could not verify project access. Retry before creating a project.' })
        : requiresSubscription ? projectCreationSubscriptionMessage : message);
    const legacyAccessEnded = isLegacyAccessEnded(snapshot);
    const legacyUpgradeAction = getLegacyUpgradeAction(snapshot);
    const upgradePath = requiresSubscription ? '/trial' : legacyAccessEnded ? legacyUpgradeAction.path : '/pricing';
    const upgradeLabel = useResolvedMessage(
        unavailable
            ? msg({ id: 'common.retry', message: 'Retry' })
            : requiresSubscription ? projectCreationTrialLabel : legacyAccessEnded
            ? legacyUpgradeAction.label
            : msg({ id: 'billing.viewPlans', message: 'View plans' })
    );

    if (!visible) {
        return null;
    }

    return (
        <Banner
            type="info"
            variant="subtle"
            dismissable={dismissable}
            onDismiss={onDismiss}
            style={style}
            testID={testID}
        >
            <Text style={[styles.text, fontsLoaded && interRegular]}>
                {resolvedMessage}{' '}
                {unavailable ? <Text
                    style={[styles.link, fontsLoaded && interSemiBold]}
                    onPress={() => {
                        void entitlementsQuery.refreshAuthoritatively().catch(() => undefined);
                    }}
                    accessibilityRole="button"
                >
                    {upgradeLabel}
                </Text> : <Link href={upgradePath} style={[styles.link, fontsLoaded && interSemiBold]}>
                    {upgradeLabel}
                </Link>}
                .
            </Text>
        </Banner>
    );
}

const styles = StyleSheet.create({
    text: {
        color: colors.text700,
        fontSize: 14,
        lineHeight: 21,
    },
    link: {
        color: colors.primaryGreenDark,
        fontSize: 14,
        fontWeight: '600',
        textDecorationLine: 'underline',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
});
